import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, Subject, catchError, of, takeUntil, tap } from 'rxjs';
import { Router } from '@angular/router';

import { ConfigService } from '../../core/config.service';
import { CaseService } from '../case-service/case.service';
import { ConnectionErrorService } from '../connection-error-service/connection-error.service';
import { toUpperCase } from '../../shared-components/utils/title-case.util';

export interface UserData {
  userId: string;
  name: string;
  modifiedBy: string;
  modifiedDate: string;
  supervisorId: string;
  loginId: string;
  serviceCode: string;
  phoneNo: string;
  routineTargetMos: number;
  enhancedTargetMos: number;
  initials: string;
  emailId: string;
  activeFlag: 'Y' | 'N';
  userType: string;
  inspectorType: string | null;
  amandaLoginId: string;
}

export interface CheckActiveResponse {
  status: 'SUCCESS' | 'FAILURE' | string;
  statusCode: number;
  errorMessage: string | null;
  data: UserData | null;
  correlationId?: string;
}

export interface ConfigPathValidation {
  pathKey: string;
  path: string;
  valid: boolean;
  reason?: string | null;
}

export interface ValidatePathsResponse {
  status: 'SUCCESS' | 'FAILURE' | string;
  statusCode: number;
  errorMessage: string | null;
  data: ConfigPathValidation[] | null;
  correlationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService implements OnDestroy {

  private readonly configService = inject(ConfigService);
  private readonly caseService = inject(CaseService);
  private readonly connectionErrorService = inject(ConnectionErrorService);

  private readonly baseUrl = `${this.configService.get().APIBaseUrl}/search`;

  // config service lives under a different base path (/cea-api/api/v1/config/...)
  private readonly configBaseUrl = `${this.configService.get().APIBaseUrl}/config`;

  // FIX: Create subjects to allow request cancellation
  private readonly checkActiveCancel$ = new Subject<void>();
  private readonly validatePathsCancel$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  readonly username = signal(toUpperCase(localStorage.getItem('Username')));
  readonly workspaceLoading = signal(false);

  setUsername(loginId: string): void {
    if (!loginId) {
      return;
    }
    localStorage.setItem('Username', loginId);
    this.username.set(toUpperCase(loginId));
  }

  clearUsername(): void {
    localStorage.removeItem('Username');
    this.username.set('');
  }

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  checkActive(email: string): Observable<CheckActiveResponse> {
    const params = new HttpParams().set('testEmail', email);

    // FIX: Cancel previous request
    this.checkActiveCancel$.next();

    return this.http
      .get<CheckActiveResponse>(
        `${this.baseUrl}/checkActive`,
        { params }
      )
      .pipe(
        // FIX: Use takeUntil to unsubscribe when cancelled
        takeUntil(this.checkActiveCancel$),
        takeUntil(this.destroy$),
        catchError((error: HttpErrorResponse) => {
          console.error('[checkActive] HTTP error:', error);

          if (error.status === 401) {
            // Authentication/authorization failure
            this.router.navigateByUrl('/unauthorized');
            // FIX: Return null instead of EMPTY to properly complete the stream
            return of(null as any);
          }

          this.connectionErrorService.navigateToErrorPage(error);

          return of(null as any);
        })
      );
  }

  validateConfigPaths(): Observable<ValidatePathsResponse> {
    // FIX: Cancel previous request
    this.validatePathsCancel$.next();

    return this.http
      .get<ValidatePathsResponse>(`${this.configBaseUrl}/validate-paths`)
      .pipe(
        // FIX: Use takeUntil to unsubscribe when cancelled
        takeUntil(this.validatePathsCancel$),
        takeUntil(this.destroy$),
        tap((response) => {
          if (response.status !== 'SUCCESS') {
            this.caseService.showToast(
              response.errorMessage || 'Failed to validate configuration paths.',
              'error',
              true,
              response.correlationId,
            );
          }
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('[validateConfigPaths] HTTP error:', error);

          this.caseService.showToast(
            'Failed to validate configuration paths.',
            'error',
            true,
            error.error?.correlationId,
          );

          // FIX: Return a safe null response instead of EMPTY
          return of({
            status: 'FAILURE',
            statusCode: error.status,
            errorMessage: error.error?.errorMessage || 'Failed to validate paths',
            data: null,
            correlationId: error.error?.correlationId,
          } as ValidatePathsResponse);
        })
      );
  }

  // FIX: Add cleanup method for app shutdown
  cleanup(): void {
    // Cancel all pending requests
    this.checkActiveCancel$.next();
    this.checkActiveCancel$.complete();

    this.validatePathsCancel$.next();
    this.validatePathsCancel$.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }

  // FIX: Implement OnDestroy
  ngOnDestroy(): void {
    this.cleanup();
  }
}